import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query-v4';
import _ from 'lodash';
import {
  ModelAvailability,
  ModelEvalEndpointDTO,
  ModelEvalEndpointWithUserInfoDTO,
  ModelResponseAndRequestDTO,
  ModelHealthMetrics,
  RunModelHealthAgentRequest,
  ModelErrorBreakdownDTO,
} from '@scale/customer-app-shared';
import API from '@scale/frontend-lib/dashboard-api';
import type { CreateCompletionResponse } from '@scale/shared/types/chat-completion';

export enum QueryNamespace {
  Models = 'models',
}

function generateQueryKey({
  namespace,
  hookName, // Uniqueness enforced by eslint-local-rules.js
  hookArguments,
}: {
  // Typechecked - must be a valid key path in QUERY_KEYS above
  namespace: QueryNamespace;
  hookName: string;
  hookArguments:
    | IArguments
    | (number | string | boolean | undefined | null | Record<string, any>)[];
}) {
  const argsAsArray = Array.isArray(hookArguments) ? hookArguments : Array.from(hookArguments);
  // NOTE: react-query already consistently hashes object arguments for us (hashKey in https://github.com/TanStack/query/blob/main/packages/query-core/src/utils.ts)
  const cleanedArguments = argsAsArray.map(arg => {
    if (Array.isArray(arg)) {
      return _.sortBy(arg);
    }
    return arg;
  });
  const queryKey = [namespace, hookName, ...cleanedArguments];
  return queryKey;
}

export function useGetCustomerModelVersions({
  customerId,
  projectId,
}: {
  customerId?: string;
  projectId?: string;
}) {
  return useQuery<ModelEvalEndpointWithUserInfoDTO[]>({
    queryKey: generateQueryKey({
      namespace: QueryNamespace.Models,
      hookName: 'useGetCustomerModelVersions',
      hookArguments: [customerId, projectId],
    }),
    queryFn: () =>
      API.genai.getCustomerModelVersions({
        customerId: customerId ?? '',
        projectId: projectId ?? '',
      }),
    enabled: !!customerId || !!projectId,
  });
}

export function useGetCustomerProjectModels(projectId: string) {
  return useQuery<{ id: string; name: string; versionName: string; removable: boolean }[]>({
    queryKey: generateQueryKey({
      namespace: QueryNamespace.Models,
      hookName: 'useGetCustomerProjectModels',
      hookArguments: [projectId],
    }),
    queryFn: () => API.genai.getModelVersionsByProject(projectId),
    enabled: !!projectId,
  });
}

export function useGetModelConfiguration(modelId: string) {
  return useQuery<ModelEvalEndpointDTO>({
    queryKey: generateQueryKey({
      namespace: QueryNamespace.Models,
      hookName: 'useGetModelConfiguration',
      hookArguments: [modelId],
    }),
    queryFn: () => API.genai.getModelConfigurationById(modelId),
    enabled: !!modelId,
  });
}

export function useGetModelAvailability({
  modelFamilyOrName,
  projectId,
  customerId,
}: {
  modelFamilyOrName?: string;
  projectId?: string;
  customerId?: string;
}) {
  return useQuery<ModelAvailability>({
    queryKey: generateQueryKey({
      namespace: QueryNamespace.Models,
      hookName: 'useGetModelAvailability',
      hookArguments: [modelFamilyOrName, projectId, customerId],
    }),
    queryFn: () => API.genai.getModelAvailability(modelFamilyOrName ?? '', projectId, customerId),
    enabled: !!modelFamilyOrName && (!!customerId || !!projectId),
  });
}

export function useGetModelsAvailability(modelFamiliesOrNames: string[], projectId: string) {
  return useQueries({
    queries: modelFamiliesOrNames.map(modelFamilyOrName => ({
      queryKey: generateQueryKey({
        namespace: QueryNamespace.Models,
        hookName: 'useGetModelAvailability',
        hookArguments: [modelFamilyOrName],
      }),
      queryFn: () => API.genai.getModelAvailability(modelFamilyOrName, projectId),
      enabled: !!modelFamilyOrName,
    })),
  });
}

export function useRemoveCustomerProjectModelVersion(projectId?: string, modelVersionId?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => API.genai.removeCustomerProjectModelVersion(projectId, modelVersionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QueryNamespace.Models] });
    },
  });
}

export function useGetModelVersionConfigurationById(modelVersionConfigurationId?: string) {
  return useQuery<ModelEvalEndpointDTO>({
    queryKey: generateQueryKey({
      namespace: QueryNamespace.Models,
      hookName: 'useGetModelVersionConfigurationById',
      hookArguments: [modelVersionConfigurationId],
    }),
    queryFn: () => API.genai.getModelVersionConfigurationById(modelVersionConfigurationId ?? ''),
    enabled: !!modelVersionConfigurationId,
  });
}

export function useGetModelLogs({
  modelVersionIdOrVersionOrFamily,
  page,
  pageSize,
  search,
  status,
  projectId,
  useCase,
  autoFetch = true,
}: {
  modelVersionIdOrVersionOrFamily?: string;
  page?: number;
  pageSize?: number;
  search?: string;
  status?: string;
  projectId?: string;
  useCase?: string;
  autoFetch?: boolean;
}) {
  return useQuery<ModelResponseAndRequestDTO[]>({
    queryKey: [
      'modelErrorLogs',
      modelVersionIdOrVersionOrFamily,
      page,
      pageSize,
      search,
      status,
      projectId,
      useCase,
    ],
    queryFn: async (): Promise<ModelResponseAndRequestDTO[]> => {
      return await API.genai.getModelLogs({
        modelVersionIdOrVersionOrFamily,
        page,
        pageSize,
        search,
        status,
        projectId,
        useCase,
      });
    },
    enabled: autoFetch && (!!modelVersionIdOrVersionOrFamily || !!projectId),
  });
}

export function useGetModelLogsCount(
  modelVersionIdOrVersionOrFamily?: string,
  search?: string,
  status?: string,
  projectId?: string,
  useCase?: string,
) {
  return useQuery<number>({
    queryKey: [
      'modelErrorLogsCount',
      modelVersionIdOrVersionOrFamily,
      search,
      status,
      projectId,
      useCase,
    ],
    queryFn: async (): Promise<number> => {
      return await API.genai.getModelLogsCount({
        modelVersionIdOrVersionOrFamily,
        search,
        status,
        projectId,
        useCase,
      });
    },
    enabled: !!modelVersionIdOrVersionOrFamily || !!projectId,
  });
}

export function useReplayModelRequest() {
  return useMutation<
    CreateCompletionResponse,
    Error,
    {
      requestId: string;
      responseId?: string;
    }
  >({
    mutationFn: async ({ requestId, responseId }) => {
      return await API.genai.replayModelRequest(requestId, responseId);
    },
  });
}

export function useGetModelVersionHealth(
  modelVersionConfigurationIdOrModelFamily: string,
  startTime: number,
  endTime: number,
  periodMinutes: number,
) {
  return useQuery<ModelHealthMetrics>({
    queryKey: [
      'modelVersionHealth',
      modelVersionConfigurationIdOrModelFamily,
      startTime,
      endTime,
      periodMinutes,
    ],
    queryFn: async (): Promise<ModelHealthMetrics> => {
      return await API.genai.getModelVersionHealth(
        modelVersionConfigurationIdOrModelFamily,
        startTime,
        endTime,
        periodMinutes,
      );
    },
    enabled: !!modelVersionConfigurationIdOrModelFamily,
  });
}

export function useRunModelHealthAgentMutation() {
  return useMutation({
    mutationFn: async ({ modelName, projectId, forceRefresh }: RunModelHealthAgentRequest) => {
      return await API.genai.runModelHealthAgent(modelName, projectId, forceRefresh);
    },
  });
}

export function useAnalyzeModelErrorsMutation() {
  return useMutation({
    mutationFn: async ({
      errorLogs,
      projectId,
      searchQuery,
    }: {
      errorLogs: ModelResponseAndRequestDTO[];
      projectId: string;
      searchQuery?: string;
    }) => {
      return await API.genai.analyzeModelErrors(errorLogs, projectId, searchQuery);
    },
  });
}

export function useGetModelErrorBreakdown(
  modelVersionIdOrVersionOrFamily?: string,
  projectId?: string,
  startTime?: number,
  useCase?: string,
) {
  return useQuery<ModelErrorBreakdownDTO>({
    queryKey: [
      'modelErrorBreakdown',
      modelVersionIdOrVersionOrFamily,
      projectId,
      startTime,
      useCase,
    ],
    queryFn: async () => {
      return await API.genai.getModelErrorBreakdown(
        modelVersionIdOrVersionOrFamily,
        projectId,
        startTime,
        useCase,
      );
    },
    enabled: !!modelVersionIdOrVersionOrFamily || !!projectId,
  });
}
